import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import CustomerStatFilterContext from './CustomerStatFilterContext';
import FilterSelectionRow from './FilterSelectionRow';
import {
    filterTableCSS,
    filterTableTitleCSS,
} from './styles/FilterSelectionDropdownMenuContainer.styles';
import { getRedReportFilterList } from '../../services/getRedReportFilterList';

function isStatFilterComplete(filter) {
    if (filter) {
        if (filter.statFilter) {
            return (
                filter.statFilter.operator &&
                filter.statFilter.number &&
                filter.statFilter.filterType &&
                filter.statFilter.statKey
            );
        }
        return true;
    }
    return false;
}

export default function FilterSelectionTable({
    applications,
    customers,
    mobile,
    platformKey,
    topDown,
    allStats,
}) {
    const [filterRows, setFilterRows] = useState([{ num: 1, filterType: '', filter: null }]);

    const {
        selectedCustomerFilterList,
        pendingFilterList,
        setPendingFilterList,
        setPendingCreateCustomFilterList,
        setPendingFilterQuery,
        clearFilters,
        setClearFilters,
        setPendingCreateCustomFilterQuery,
        pendingFilterQuery,
        setNoEmptyRows,
        setErrorMsg,
    } = useContext(CustomerStatFilterContext);

    useEffect(() => {
        if (clearFilters) {
            setFilterRows([{ num: 1, filterType: '', filter: null }]);
            setClearFilters(false);
        } else if (selectedCustomerFilterList === 'redReport') {
            setFilterRows(getRedReportFilterList(allStats));
        } else if (pendingFilterList) {
            if (pendingFilterList.length === 0) {
                setFilterRows([{ num: 1, filterType: '', filter: null }]);
            } else {
                const rows = [];
                for (let i = 0; i < pendingFilterList.length; i += 1) {
                    const filter = pendingFilterList[i];
                    let filterType = '';
                    if (filter?.appKeys && filter?.appKeys.length !== 0) {
                        filterType = 'Application';
                    }
                    if (filter.customerIds && filter.customerIds.length !== 0) {
                        filterType = 'Customers';
                    }
                    if (filter.vaultBasics) {
                        filterType = 'Basics';
                    }
                    if (filter.vaultSizes && filter.vaultSizes.length !== 0) {
                        filterType = 'Vault Size';
                    }
                    if (filter.podNames && filter.podNames.length !== 0) {
                        filterType = 'POD';
                    }
                    if (filter.statFilter) {
                        filterType = `Stats ${filter.statFilter.statKey}`;
                    }
                    if (filter.customerSizes) {
                        filterType = 'Customer Size';
                    }
                    if (filter.regions) {
                        filterType = 'Customer Region';
                    }
                    if (filter.industries) {
                        filterType = 'Customer Industry';
                    }
                    if (filter.segments) {
                        filterType = 'Customer Segment';
                    }
                    rows.push({ num: i + 1, filterType, filter });
                }
                setFilterRows(rows);
            }
        }
        // update only when a user chooses a different list in radio selection or clears filters
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCustomerFilterList, clearFilters]);

    const handleSetFilters = (num, filter, replace, filterType) => {
        if (filter) {
            const filterList = pendingFilterList ? [...pendingFilterList] : [];
            const newFilter = !filterRows[num - 1].filter;

            if (!replace && newFilter) {
                filterList.splice(num - 1, 0, filter);
                const newFilterQuery =
                    filterList.length !== 1
                        ? `${pendingFilterQuery} AND ${filterList.length}`
                        : '1';

                setPendingFilterQuery(newFilterQuery);
                if (selectedCustomerFilterList === 'create_custom') {
                    setPendingCreateCustomFilterQuery(newFilterQuery);
                }
            } else {
                filterList[num - 1] = filter;
            }

            setPendingFilterList(filterList);
            if (selectedCustomerFilterList === 'create_custom') {
                setPendingCreateCustomFilterList(filterList);
            }
        } else if (filterRows[num - 1].filter) {
            // user updated filter to be empty
            const newFilterList = [...pendingFilterList];
            newFilterList.splice(num - 1, 1);
            setPendingFilterList(newFilterList);

            const newFilterQuery = newFilterList.map((_, index) => `${index + 1}`).join(' AND ');
            setPendingFilterQuery(newFilterQuery);

            if (selectedCustomerFilterList === 'create_custom') {
                setPendingCreateCustomFilterList(newFilterList);
                setPendingCreateCustomFilterQuery(newFilterQuery);
            }
        }

        const newFilterRows = filterRows.map((row, index) =>
            index === num - 1 ? { num, filterType, filter } : row,
        );
        setFilterRows(newFilterRows);

        setNoEmptyRows(
            !newFilterRows.some(
                (item) =>
                    item.filterType === '' ||
                    item.filter === null ||
                    !isStatFilterComplete(item.filter),
            ),
        );
        setErrorMsg('');
    };

    const addFilterRow = (num) => {
        const newFilterRows = [
            ...filterRows.slice(0, num),
            { num: num + 1, filterType: '', filter: null },
            ...filterRows.slice(num).map((row) => ({
                ...row,
                num: row.num + 1,
            })),
        ];
        setFilterRows(newFilterRows);
        setNoEmptyRows(false);
    };

    const deleteFilterRow = (num) => {
        if (filterRows.length === 1) {
            setFilterRows([{ num: 1, filterType: '', filter: null }]);
            setPendingFilterList([]);
            setPendingFilterQuery('');
            if (selectedCustomerFilterList === 'create_custom') {
                setPendingCreateCustomFilterList([]);
                setPendingCreateCustomFilterQuery('');
            }
            return;
        }

        const newFilterRows = filterRows
            .filter((_, index) => index !== num - 1)
            .map((row, index) => ({
                ...row,
                num: index + 1,
            }));

        setFilterRows(newFilterRows);

        if (filterRows[num - 1]?.filter) {
            const updatedFilterList = pendingFilterList.filter((_, index) => index !== num - 1);
            setPendingFilterList(updatedFilterList);
            if (selectedCustomerFilterList === 'create_custom') {
                setPendingCreateCustomFilterList(updatedFilterList);
            }
        }

        const newFilterQuery = newFilterRows
            .filter((item) => item.filterType !== '' && item.filter !== null)
            .map((_, index) => `${index + 1}`)
            .join(' AND ');

        setPendingFilterQuery(newFilterQuery);
        if (selectedCustomerFilterList === 'create_custom') {
            setPendingCreateCustomFilterQuery(newFilterQuery);
        }

        setNoEmptyRows(
            !newFilterRows.some(
                (item) =>
                    item.filterType === '' ||
                    item.filter === null ||
                    !isStatFilterComplete(item.filter),
            ),
        );
    };

    return (
        <table css={filterTableCSS}>
            <thead>
                <tr>
                    <th colSpan={2}>
                        <span css={filterTableTitleCSS}>Select attribute to filter by.</span>
                    </th>
                </tr>
            </thead>
            <tbody>
                {filterRows.map(({ num, filterType, filter }) => (
                    <FilterSelectionRow
                        key={num}
                        filterNumber={num}
                        applications={applications}
                        customers={customers}
                        initialFilterType={filterType}
                        filter={filter}
                        handleSetFilters={handleSetFilters}
                        addFilterRow={addFilterRow}
                        deleteFilterRow={deleteFilterRow}
                        mobile={mobile}
                        platformKey={platformKey}
                        totalSelectedRows={filterRows.length}
                        topDown={topDown}
                        allStats={allStats}
                    />
                ))}
            </tbody>
        </table>
    );
}
FilterSelectionTable.propTypes = {
    allStats: PropTypes.arrayOf(
        PropTypes.shape({
            compoundKey: PropTypes.string,
            label: PropTypes.string,
            statKey: PropTypes.string,
            type: PropTypes.string,
        }),
    ),
    applications: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string,
            label: PropTypes.string,
            stats: PropTypes.arrayOf(
                PropTypes.shape({
                    applicationKey: PropTypes.string,
                    key: PropTypes.string,
                    label: PropTypes.string,
                }),
            ),
        }),
    ),
    customers: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            label: PropTypes.string,
        }),
    ),
    mobile: PropTypes.bool,
    platformKey: PropTypes.string,
    topDown: PropTypes.bool,
};
